import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { ZoomIn, ZoomOut, NavigateBefore, NavigateNext } from '@mui/icons-material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PDFViewerProps {
  pdfUrl: string;
  width?: number;
  onPageChange?: (page: number, totalPages: number) => void;
  onDocumentLoad?: (numPages: number) => void;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfUrl,
  width = 800,
  onPageChange,
  onDocumentLoad,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setLoading(false);
      onDocumentLoad?.(numPages);
    },
    [onDocumentLoad]
  );

  const changePage = useCallback(
    (offset: number) => {
      const newPage = pageNumber + offset;
      if (newPage >= 1 && newPage <= numPages) {
        setPageNumber(newPage);
        onPageChange?.(newPage, numPages);
      }
    },
    [pageNumber, numPages, onPageChange]
  );

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* 컨트롤 바 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          p: 1,
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <IconButton onClick={() => changePage(-1)} disabled={pageNumber <= 1} size="small">
          <NavigateBefore />
        </IconButton>
        <Typography variant="body2">
          {pageNumber} / {numPages}
        </Typography>
        <IconButton onClick={() => changePage(1)} disabled={pageNumber >= numPages} size="small">
          <NavigateNext />
        </IconButton>
        <Box sx={{ mx: 1, borderLeft: '1px solid', borderColor: 'grey.300', height: 24 }} />
        <IconButton onClick={zoomOut} disabled={scale <= 0.5} size="small">
          <ZoomOut />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton onClick={zoomIn} disabled={scale >= 3.0} size="small">
          <ZoomIn />
        </IconButton>
      </Box>

      {/* PDF 문서 */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'grey.300',
          bgcolor: 'grey.50',
        }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width,
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={null}
          error={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error">PDF를 불러올 수 없습니다.</Typography>
            </Box>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={width * scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </Box>
    </Box>
  );
};

export default PDFViewer;
